/**
 * Hybrid RSA + AES-256-GCM encryption for outgoing request payloads.
 *
 * Mirrors the backend contract in fast_api/crypto/crypto_utils.py:
 *   - A per-request AES-256-GCM key/IV encrypts the JSON body ->
 *     Base64(ciphertext || tag), sent as the request body `value`.
 *   - That key/IV is RSA-OAEP(SHA-256)-wrapped with the server public key ->
 *     Base64, sent in the `x-cryptokey-details` header.
 *
 * Uses Web Crypto (window.crypto.subtle), which requires a secure context:
 * https:// or http://localhost (NOT plain http:// on a LAN IP).
 */
import { GCC_CRYPTO_PUBLIC_KEY } from '../runtimeConfig';

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

let _publicKeyPromise = null;

/**
 * Import the RSA public key from runtime config (memoized for the session).
 * The key is a non-secret base64 DER (SPKI) string shipped with the app
 * config (GCC_CRYPTO_PUBLIC_KEY) — no network round-trip is needed.
 * @returns {Promise<CryptoKey>}
 */
export function getServerPublicKey() {
  if (!_publicKeyPromise) {
    _publicKeyPromise = (async () => {
      // Accept either a bare base64 DER or a full PEM, stripping any
      // header/footer/whitespace so both forms import cleanly.
      const b64 = (GCC_CRYPTO_PUBLIC_KEY || '')
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\s+/g, '');
      if (!b64) {
        throw new Error('RSA public key is not configured (GCC_CRYPTO_PUBLIC_KEY)');
      }
      const der = base64ToArrayBuffer(b64);
      return window.crypto.subtle.importKey(
        'spki',
        der,
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt'],
      );
    })().catch((err) => {
      // Don't cache a failed import — allow a retry on the next call.
      _publicKeyPromise = null;
      throw err;
    });
  }
  return _publicKeyPromise;
}

/**
 * Hybrid-encrypt a JSON-serializable payload.
 *
 * @param {object} obj - plaintext payload
 * @returns {Promise<{ value: string, cryptoKeyDetails: string }>}
 *   value: Base64(AES-GCM ciphertext||tag) for the request body.
 *   cryptoKeyDetails: Base64(RSA-OAEP-wrapped {key, iv}) for the header.
 */
export async function encryptPayload(obj) {
  const subtle = window.crypto.subtle;

  // Per-request AES-256 key + 12-byte IV.
  const aesKey = await subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt'],
  );
  const rawKey = await subtle.exportKey('raw', aesKey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  // AES-GCM-encrypt the body JSON (Web Crypto appends the 16-byte tag).
  const plaintext = new TextEncoder().encode(JSON.stringify(obj));
  const ciphertext = await subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext);
  const value = arrayBufferToBase64(ciphertext);

  // RSA-OAEP-wrap the {key, iv} details for the header.
  const pubKey = await getServerPublicKey();
  const keyDetails = new TextEncoder().encode(
    JSON.stringify({
      key: arrayBufferToBase64(rawKey),
      iv: arrayBufferToBase64(iv.buffer),
    }),
  );
  const wrapped = await subtle.encrypt({ name: 'RSA-OAEP' }, pubKey, keyDetails);
  const cryptoKeyDetails = arrayBufferToBase64(wrapped);

  return { value, cryptoKeyDetails };
}
